import {
  capitalizeFirstLetter,
  customDeliveryUrl,
  ApiService,
  getCustomWebsiteUrl,
  randomItem,
  EMAIL_TYPES
} from 'shared/helpers/index';
import useAuth from 'shared/hooks/useAuth';
import useSales from 'shared/hooks/useSales';
import useBook from 'shared/hooks/useBook';
import type { AiPayloadForDeliveryEmail, PageData } from '../types';
import { useQuery } from 'react-query';
import type { AiEmailSettings, DeliveryEmail, EmailType } from 'shared/context/SalesContext';
import { cloneDeep } from 'lodash';

interface DeliveryEmailPayload {
  sales_page: number;
  preview_text: string;
  headline: string;
  body: string;
  is_published?: boolean;
}

/**
 * @function useDeliveryEmail
 *
 * @description This hook is used for handling the delivery email for the book.
 * Here we have methods to get the delivery email data, initialize the delivery email,
 * and update the delivery email. Currently we support 2 versions for 1.0 and 2.0.
 */
const useDeliveryEmail = () => {
  const { bookName } = useBook();
  const {
    downloadUrl,
    salesPageValues,
    deliveryPageValues,
    ai,
    subDomain,
    deliveryToken,
    setDeliveryEmail,
    metaTags,
    saleEmailTemplateCollection,
    saleEmailTemplateStaticCollection,
    setSaleEmailTemplateCollection,
    setSaleEmailTemplateStaticCollection,
    upsellDetails,
    upsellPrice,
    isDashboard
  } = useSales();
  const { user } = useAuth();

  useQuery(`/sales/sale-email-templates/`, () => ApiService.get(`/sales/sale-email-templates/`), {
    select: (response) => response.data,
    onSuccess: (data) => setSaleEmailTemplateCollection(data),
    refetchOnWindowFocus: false,
    enabled: isDashboard
  });

  useQuery(
    `/sales/sale-email-templates-static/`,
    () => ApiService.get(`/sales/sale-email-templates-static/`),
    {
      select: (response) => response.data,
      onSuccess: (data) => setSaleEmailTemplateStaticCollection(data),
      refetchOnWindowFocus: false,
      enabled: isDashboard
    }
  );

  const generateAiForDeliveryEmail = async (data: AiPayloadForDeliveryEmail) => {
    const response = await ApiService.post(`sales/sales-page/generate-ai-email/`, {
      prompt: data.prompt,
      mapping: data.mapping,
      type: data.type,
      retry_count: data.retry_count,
      language: data.language,
      source: data.source || 'database',
      funnel_name: data.funnel_name,
      email_type: data.email_type || 'delivery',
      prompt_input: data.prompt_input || '',
      alternation_version: data.alternation_version || 1
    });
    return response.data;
  };

  const cleanUpEmailManually = (email: { subject: string; preview: string; body: string }) => {
    const deliveryEmailData = cloneDeep(email);
    let preview_text = deliveryEmailData?.preview || '';
    if (preview_text.length >= 180) {
      const index = preview_text.indexOf('.');
      if (index !== -1) {
        preview_text = preview_text.slice(0, index + 1);
      }
      if (preview_text.length >= 180) {
        preview_text = preview_text.slice(0, 179);
      }
    }
    let headline = deliveryEmailData?.subject;
    if (headline?.length >= 120) {
      const index = headline.indexOf('.');
      if (index !== -1) {
        headline = headline.slice(0, index + 1);
      }
      if (headline.length >= 120) {
        headline = headline.slice(0, 179);
      }
    }

    return {
      preview_text: preview_text.replace(/<[^>]*>/g, ''),
      headline: capitalizeFirstLetter(headline || '').replace(/<[^>]*>/g, ''),
      body: deliveryEmailData.body || ''
    };
  };

  const initializeDeliveryEmail = async (
    salesPageId: number,
    funnel: string,
    forceInitialization = false,
    salesPageValuesUpdated: PageData | undefined | null = undefined,
    deliveryPageValuesUpdated: PageData | undefined | null = undefined
  ) => {
    const deliveryEmailResponse = await ApiService.get(
      `/sales/book-delivery-email/${salesPageId}/`
    );
    const emails = cloneDeep(deliveryEmailResponse.data) as DeliveryEmail[];
    for (const emailType of EMAIL_TYPES) {
      const email = emails.find((email) => email.email_type === emailType.value);
      if (email?.is_published && !forceInitialization) continue;
      const templates = saleEmailTemplateCollection?.filter(
        (template) => template.funnel_name === funnel && template.email_type === emailType.value
      );
      const template = randomItem(templates);
      if (!template) return;
      salesPageValuesUpdated = salesPageValuesUpdated || salesPageValues;
      deliveryPageValuesUpdated = deliveryPageValuesUpdated || deliveryPageValues;
      if (!salesPageValuesUpdated || !deliveryPageValuesUpdated) return;
      const deliveryEmailData = await deliveryEmailGenerator(
        template.subject,
        template.preview,
        template.body,
        {
          funnel_name: funnel,
          email_type: emailType.value,
          source: 'database',
          prompt_input: ai,
          alternation_version: template.alternation_version || 1,
          body: template.body
        },
        salesPageValuesUpdated,
        deliveryPageValuesUpdated,
        metaTags
      );
      const cleanedEmail = cleanUpEmailManually({
        preview: deliveryEmailData.preview,
        subject: deliveryEmailData.subject,
        body: deliveryEmailData.body
      });

      const emailExists = emails.find((email) => email.email_type === emailType.value);
      if (emailExists) {
        emailExists.preview_text = cleanedEmail.preview_text;
        emailExists.headline = cleanedEmail.headline;
        emailExists.body = cleanedEmail.body;
      } else {
        const newEmail: DeliveryEmail = {
          sales_page: salesPageId,
          preview_text: cleanedEmail.preview_text,
          headline: cleanedEmail.headline,
          body: cleanedEmail.body,
          funnel_name: funnel,
          email_type: emailType.value as EmailType,
          is_published: false
        };
        emails.push(newEmail);
      }
    }

    const updatedDeliveryResponse = await updateDeliveryEmail(salesPageId, emails);
    setDeliveryEmail(updatedDeliveryResponse.data);
  };

  const updateDeliveryEmail = async (
    salesPageId: number,
    deliveryEmailPayload: DeliveryEmailPayload[]
  ) => {
    return await ApiService.patch(
      `/sales/book-delivery-email/${salesPageId}/`,
      deliveryEmailPayload
    );
  };

  const cleanSalesOutput = (salesOutput: string) => {
    return salesOutput
      .replace(/<\/?[^>]+(>|$)/g, '')
      .replaceAll('"', '')
      .replace('\n', '');
  };

  function parseTemplate(template: string, data: Record<string, string>) {
    if (!template) return '';
    // Update AI_RUNS first
    template = template?.replace(
      /\[PDF_LINK:(.*?)\]/g,
      `<a href="${downloadUrl}" rel="noopener noreferrer" target="_blank">$1</a>`
    );
    template = template.replace(
      /\[DELIVERY_URL:(.*?)\]/g,
      `<a href="${customDeliveryUrl(subDomain, deliveryToken)}" rel="noopener noreferrer" target="_blank">$1</a>`
    );
    const upsellUrl = metaTags['isStaff'] ? 'UPSELL_URL' : upsellDetails?.delivery_url || '';
    template = template.replace(
      /\[UPSELL_URL:(.*?)\]/g,
      `<a href="${upsellUrl}" rel="noopener noreferrer" target="_blank">$1</a>`
    );
    template = template.replace(/\[(.*?):(.*?)\]/g, (_, urlKey, linkText) => {
      // Check if the URL key exists in the data and use its value or a default #
      const url = data[urlKey] || '#';
      return `<a href="${url}" rel="noopener noreferrer" target="_blank">${linkText}</a>`;
    });

    return template?.replace(/{(.*?)}/g, (_, variableGroup) => {
      const parts = variableGroup.split('|').map((v: string) => v.trim());
      let defaultText = '';
      let leftText = '';
      let rightText = '';

      for (const part of parts) {
        if (part.startsWith('left(')) {
          leftText = part.slice(5, -1);
        } else if (part.startsWith('right(')) {
          rightText = part.slice(6, -1);
        }
      }

      for (const part of parts) {
        if (part.startsWith('default(')) {
          defaultText = part.slice(8, -1);
        } else if (
          Object.prototype.hasOwnProperty.call(data, part) &&
          data[part] !== undefined &&
          data[part] !== null
        ) {
          return leftText + data[part] + rightText;
        }
      }
      if (defaultText) {
        return leftText + defaultText + rightText;
      }
      return defaultText.replaceAll('&nbsp;', ' ');
    });
  }

  function parseSalesDataForEmail(data: PageData, salesType: 'sales' | 'delivery') {
    const result = {} as Record<string, string>;
    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (typeof value !== 'object') return;
      const eventMetaKeys = [
        'date',
        'time',
        'noDates',
        'eventUrl',
        'calendarUrl',
        'timezone',
        'localCity',
        'localAddress',
        'isRecurring',
        'peopleLimit',
        'zoomPassword',
        'isEventOnline',
        'recurringType'
      ];
      eventMetaKeys.forEach((metaKey) => {
        if (value.meta?.[metaKey]) {
          result[`${key}__meta__${metaKey}`] = value.meta[metaKey];
        }
      });

      const upsellKeys = [
        'currency',
        'upsell_type',
        'delivery_url',
        'product_name',
        'order_description'
      ];
      upsellKeys.forEach((key) => {
        if (upsellDetails && upsellDetails[key as keyof typeof upsellDetails]) {
          result[`UPSELL_${key.toUpperCase()}`] =
            String(upsellDetails[key as keyof typeof upsellDetails]) || '';
        }
      });

      if (value.meta?.['url']) {
        result[`${key}__meta__url`] = value.meta['url'];
      }
      if (value.meta?.['eventUrl']) {
        result[`${key}__meta__eventUrl`] = value.meta['eventUrl'];
      }

      if (value.meta?.['isEventOnline'] === 'local') {
        result[`${key}__meta__eventUrl`] =
          value.meta['localAddress'] + ', ' + value.meta['localCity'];
      }

      if (value.meta?.['calendarUrl']) {
        result[`${key}__meta__calendarUrl`] = value.meta['calendarUrl'];
      }

      if (value.title?.val) {
        result[`${key}__title`] = cleanSalesOutput(value.title.val);
        result[`${salesType}_${key}__title`] = cleanSalesOutput(value.title.val);
      }
      if (value.content?.val) {
        result[`${key}__content`] = cleanSalesOutput(value.content.val);
        result[`${salesType}_${key}__content`] = cleanSalesOutput(value.content.val);
      }
      if (value.button?.val) {
        result[`${key}__button`] = cleanSalesOutput(value.button.val);
        result[`${salesType}_${key}__content`] = cleanSalesOutput(value.button.val);
      }
      if (!value.benefits) return;
      value.benefits.forEach((benefit, index) => {
        if (benefit.title?.val) {
          result[`${key}__benefits__${index}__title`] = cleanSalesOutput(benefit.title.val);
          result[`${salesType}_${key}__benefits__${index}__title`] = cleanSalesOutput(
            benefit.title.val
          );
        }
        if (benefit.content?.val) {
          result[`${salesType}_${key}__benefits__${index}__content`] = cleanSalesOutput(
            benefit.content.val
          );
        }
        if (benefit.button?.val) {
          result[`${salesType}_${key}__benefits__${index}__button`] = cleanSalesOutput(
            benefit.button.val
          );
        }
      });
    });

    return result;
  }

  const deliveryEmailGenerator = async (
    headline: string,
    preview: string,
    body: string,
    aiSettings: AiEmailSettings,
    salesData: PageData,
    deliveryData: PageData,
    metaTags: Record<string, string | number | string[]>
  ) => {
    let aiDataMapping = {} as Record<string, string>;
    try {
      aiDataMapping = await generateAiForDeliveryEmail({
        prompt: aiSettings.prompt,
        mapping: aiSettings.value_mapping,
        type: aiSettings.type_mapping,
        source: aiSettings.source,
        funnel_name: aiSettings.funnel_name,
        email_type: aiSettings.email_type || 'delivery',
        prompt_input: aiSettings.prompt_input || '',
        alternation_version: aiSettings.alternation_version || 1
      });
    } catch (e) {
      console.log('Error Generating AI Data for Delivery Email', e);
    }
    const staticEmailData = saleEmailTemplateStaticCollection?.find(
      (template) =>
        template.funnel_name === aiSettings.funnel_name && template.email_type === 'delivery'
    );
    const staticDataMapping = (staticEmailData?.data || {}) as Record<string, string>;

    // Handle field fallback logic
    const processedAiDataMapping = { ...aiDataMapping };

    // Check field_1 and field_2 individually
    if (!processedAiDataMapping['field_1'] || processedAiDataMapping['field_1'].trim() === '') {
      processedAiDataMapping['field_1'] = staticDataMapping['field_1'] || '';
    }

    if (!processedAiDataMapping['field_2'] || processedAiDataMapping['field_2'].trim() === '') {
      processedAiDataMapping['field_2'] = staticDataMapping['field_2'] || '';
    }

    const matches = [...aiSettings.body.matchAll(/\{field_(\d+)\}/g)];
    const numbers = matches.map((match) => parseInt(match[1], 10));

    let hasFailed = false;
    numbers.forEach((number) => {
      if (
        aiDataMapping[`field_${number}`] === undefined ||
        aiDataMapping[`field_${number}`].trim() === ''
      ) {
        hasFailed = true;
      }
    });

    const salesDataMapping = parseSalesDataForEmail(salesData, 'sales');
    const deliveryDataMapping = parseSalesDataForEmail(deliveryData, 'delivery');
    const dataMapping = { ...salesDataMapping, ...deliveryDataMapping, ...processedAiDataMapping };
    dataMapping['PRODUCT.NAME'] = bookName || 'Sample Product';
    dataMapping['USER.NAME'] = user?.entity?.name || '';
    dataMapping['USER.BUSINESS_NAME'] = user?.business_name || '';
    dataMapping['USER.EMAIL'] = user?.business_email || user?.email || '';
    dataMapping['SALES_URL'] = customDeliveryUrl(subDomain, deliveryToken);
    dataMapping['DELIVERY_URL'] = getCustomWebsiteUrl(subDomain);
    if (metaTags['amazon_url']) {
      dataMapping['AMAZON_URL'] = (metaTags['amazon_url'] as string) || '';
    }
    dataMapping['PURCHASE_DATE'] = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    dataMapping['UPSELL_PRICE'] = upsellPrice || '';
    if (metaTags['isStaff']) {
      dataMapping['UPSELL_PRICE'] = '$19.99';
    }

    const headlineResult = parseTemplate(headline, dataMapping);
    const previewResult = parseTemplate(preview, dataMapping);
    const bodyResult = parseTemplate(hasFailed ? staticDataMapping['field_3'] : body, dataMapping);

    return {
      subject: headlineResult,
      preview: previewResult,
      body: bodyResult
    };
  };

  return {
    updateDeliveryEmail,
    initializeDeliveryEmail,
    deliveryEmailGenerator,
    parseSalesDataForEmail
  };
};

export default useDeliveryEmail;
